/* eslint-disable @typescript-eslint/no-explicit-any */
import CardGroup from 'containers/NewWordsFrame/CardGroup';
import {TTooltipCur} from 'containers/Tooltip/Container';
import * as d3 from 'd3';
import {debuggableClass, memo} from 'utils/decorators';

export type ViewBoxComps = [number, number, number, number];

@debuggableClass
export default class SvgObject {
  static boundingClientRectCoeff = 0;
  constructor(
    public svgText: string,
    public parentDiv: HTMLDivElement,
    public isMobile: boolean,
    public setTooltipCur: (newTooltipCur: TTooltipCur | null) => void,
  ) {}
  @memo get svgRoot(): SVGSVGElement {
    return this.parentDiv.getElementsByTagName('svg')[0];
  }
  @memo get svgRootSel() {
    return d3.select(this.svgRoot);
  }
  @memo get svgRootBboxHeight() {
    return this.svgRootSel.node()!.getBBox().height;
  }
  @memo get cardGroupElems() {
    this.svgRootSel.select('#comment').remove();
    return this.svgRootSel.selectAll('#new_words_box > g').nodes() as SVGGElement[];
  }
  @memo get animationDelay() {
    return 200;
  }
  @memo get cardGroups(): CardGroup[] {
    return this.cardGroupElems.map(
      (g, no, arr) =>
        new CardGroup(
          this,
          g,
          () => Number(new Date()) + Math.random() * ((no / arr.length) * 500), // (no === 0 ? 0 : this.cardGroups[no - 1].startTime) + no * this.animationDelay,
        ),
    );
  }
  get viewBoxComps() {
    return this.svgRootSel
      .attr('viewBox')
      .split(' ')
      .map(v => Number(v)) as ViewBoxComps;
  }
  set viewBoxComps(newComps: ViewBoxComps) {
    this.svgRootSel.attr('viewBox', newComps.map(v => v.toString()).join(' '));
  }
  get viewBoxCenter() {
    const [x0, y0, w, h] = this.viewBoxComps;
    return [x0 + w / 2, y0 + h / 2];
  }
  @memo get unitRect() {
    return this.svgRootSel
      .append('rect')
      .attr('id', 'unit-rect')
      .attr('width', 100)
      .attr('height', 100)
      .attr('x', 0)
      .attr('y', 0)
      .attr('fill', 'red')
      .node() as SVGRectElement;
  }
  unmountUnitRect() {
    this.svgRootSel.selectAll('#unit-rect').remove();
    return this;
  }
  @memo get unitRectBboxW() {
    return this.unitRect.getBoundingClientRect().width;
  }
  introTooltipSel() {
    return this.svgRootSel.select('#tooltip').style('display', 'none');
  }
  setBoundingClientRectCoeff() {
    SvgObject.boundingClientRectCoeff = 100 / this.unitRectBboxW;
    this.unmountUnitRect();
    return this;
  }
  padViewBoxH(leftPad: number, rightPad: number) {
    const [x0, y0, w, h] = this.viewBoxComps;
    this.viewBoxComps = [x0 + leftPad, y0, w - leftPad - rightPad, h];
    return this;
  }
  manuallyRemoveTags() {
    this.svgRootSel.select("[id='Rectangle 36727']").remove();
    return this;
  }
  removeSvgSize() {
    this.svgRootSel.attr('width', null).attr('height', null);
    return this;
  }
  removeTextIds() {
    this.svgRootSel.selectAll('text').attr('id', null);
    return this;
  }
  setOpacity(opacity: number) {
    this.parentDiv.style.opacity = opacity.toString();
    return this;
  }
  mount() {
    this.parentDiv.innerHTML = this.svgText;
    return this;
  }
  initialize() {
    this.mount()
      .removeSvgSize()
      .setBoundingClientRectCoeff()
      .removeTextIds()
      .manuallyRemoveTags()
      .initAnimationsState();
    this.introTooltipSel();
    if (this.isMobile) {
      this.padViewBoxH(-10, -10);
    }
    return this;
  }
  initAnimationsState() {
    this.cardGroups.forEach(pg => pg.setInitialState());
    return this;
  }
  startAnimations() {
    let count = 0;
    const observer = new IntersectionObserver(
      a => {
        const ratio = a[0].intersectionRatio;
        this.cardGroups.forEach(pg => {
          if (pg.showIntersectionRatio > ratio) return;
          if ((pg as any).started) return;
          (pg as any).started = true;
          console.log('start', count++, pg);
          pg.start();
        });
      },
      {
        threshold: new Array(101).fill(0).map((_v, no, arr) => no / (arr.length - 1)),
      },
    );
    observer.observe(this.svgRoot);
    return this;
  }
  activate() {
    return this.setOpacity(0)
      .initialize()
      .setOpacity(1)
      .startAnimations();
    // .log('cardGroups');
  }
  log(prop: keyof this) {
    console.log((this as any)[prop]);
    return this;
  }
}
